import { UserGroupName, UserRole } from "../../../api/Users";
import { useAuth } from "react-oidc-context";

export enum Permission {
    ACCESS_DASHBOARD = 'ACCESS_DASHBOARD',
    LIST_CASES = 'LIST_CASES',
    ASSIGN_SA = 'ASSIGN_SA',
    ASSIGN_IO =  'ASSIGN_IO',
    OPEN_CASE = 'OPEN_CASE',
    CAN_FILTER_INVESTIGATOR = 'CAN_FILTER_INVESTIGATOR',
    DOWNLOAD_CASES = 'DOWNLOAD_CASES',
    OBSERVATIONS_BY_AUTHORITY = 'OBSERVATIONS_BY_AUTHORITY',
    EDIT_PARTICIPANT = 'EDIT_PARTICIPANT',
    ACCESS_RECOMMENDATIONS = 'ACCESS_RECOMMENDATIONS',
    ACCESS_INVESTIGATION_REPORT = 'ACCESS_INVESTIGATION_REPORT',
    ACCESS_CASE_REPORTS = 'ACCESS_CASE_REPORTS',
    ACCESS_INVESTIGATION='ACCESS_INVESTIGATION',
    ACCESS_CASE_RIGHT_SIDEBAR='ACCESS_CASE_RIGHT_SIDEBAR',
    ACCESS_INTERNAL_REPORTS='ACCESS_INTERNAL_REPORTS'
}

const REPORTING_PERMISSIONS: Permission[] = [
    Permission.ACCESS_INVESTIGATION_REPORT,
    Permission.ACCESS_CASE_REPORTS,
];

const ADMIN_PERMISSIONS: Permission[] = [
    Permission.ACCESS_DASHBOARD,
    Permission.LIST_CASES,
    Permission.ASSIGN_SA,
    Permission.ASSIGN_IO,
    Permission.OPEN_CASE,
    Permission.CAN_FILTER_INVESTIGATOR,
    Permission.DOWNLOAD_CASES,
    Permission.OBSERVATIONS_BY_AUTHORITY,
    Permission.EDIT_PARTICIPANT,
    Permission.ACCESS_RECOMMENDATIONS,
    ...REPORTING_PERMISSIONS,
    Permission.ACCESS_INVESTIGATION,
    Permission.ACCESS_CASE_RIGHT_SIDEBAR,
    Permission.ACCESS_INTERNAL_REPORTS

]

const AUTH_L1_PERMISSIONS: Permission[] = [
  Permission.LIST_CASES,
  Permission.OPEN_CASE,
  Permission.OBSERVATIONS_BY_AUTHORITY,
  Permission.ACCESS_INVESTIGATION_REPORT,
  Permission.ACCESS_INVESTIGATION
];

const INVESTIGATOR_PREMISSIONS: Permission[] = [
    Permission.LIST_CASES,
    Permission.OPEN_CASE,
    Permission.ACCESS_INVESTIGATION_REPORT,
    Permission.ACCESS_INVESTIGATION
]

const CASE_INITIATOR_PREMISSIONS: Permission[] = [
    Permission.ACCESS_DASHBOARD,
    Permission.LIST_CASES,
    Permission.OPEN_CASE,
    Permission.ACCESS_RECOMMENDATIONS,
    Permission.ACCESS_INTERNAL_REPORTS
]

const READ_ONLY_PERMISSIONS: Permission[] = [
    Permission.ACCESS_DASHBOARD,
    Permission.LIST_CASES,
    Permission.OPEN_CASE,
    Permission.ACCESS_RECOMMENDATIONS,
    ...REPORTING_PERMISSIONS,
    Permission.ACCESS_INVESTIGATION,
    Permission.ACCESS_CASE_RIGHT_SIDEBAR,
    Permission.ACCESS_INTERNAL_REPORTS
]


export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.CANSO_ADMIN]: ADMIN_PERMISSIONS,
  [UserRole.ADMIN]: ADMIN_PERMISSIONS,
  [UserRole.BUSINESS_ADMIN]: ADMIN_PERMISSIONS,
  [UserRole.PLATFORM_ADMIN]: ADMIN_PERMISSIONS,
  [UserRole.SUPERVISING_AUTH_L2]: ADMIN_PERMISSIONS,
  [UserRole.SUPERVISING_AUTH_L1]: AUTH_L1_PERMISSIONS,
  [UserRole.CASE_INITIATOR]: CASE_INITIATOR_PREMISSIONS,
  [UserRole.INVESTIGATOR]: INVESTIGATOR_PREMISSIONS,
  [UserRole.VIEWER]: READ_ONLY_PERMISSIONS,
  [UserRole.COLLABORATOR]: READ_ONLY_PERMISSIONS,
  [UserRole.AUDITOR]: READ_ONLY_PERMISSIONS
};

export const useAbility = () => {
  const { user } = useAuth();
  
  const can = (permission: Permission): boolean => {
    if (!user) return false;
    
    const roleStr = (user.profile.realm_access as any)?.roles[0];
    const userRole = roleStr? roleStr as UserRole : undefined;

    if(!userRole) return false;

    return ROLE_PERMISSIONS[userRole].includes(permission);
  };

  return { can };
};

export const useLoggedInUserId = () => {
    const { user } = useAuth();

    if(!user){
        return "";
    }

    const userId = user.profile.sub as string;

    return userId;
}

export const useLoggedInEmailId = () => {
    const { user } = useAuth();

    if(!user){
        return "";
    }

    const userId = user.profile.email as string;

    return userId;
}

export const useUserGroupMembership = () => {
    const { user } = useAuth();
    if(!user){
        const isFrmdAdmin = false;
        return { isFrmdAdmin };
    }

    const tenant_id = user.profile.tenant_id as string;
    const groups = user.profile.groups as string[] || [];

    const frmdAdminGroup = `${tenant_id}-${UserGroupName.FRMD_ADMINS}`
    const isFrmdAdmin = groups.includes(frmdAdminGroup);

    return { isFrmdAdmin }
}
